import { ResponsePayload, RetryOptions } from 'common/types';

import { type RequestOptions } from '@frontend/api';

import { FUSION_LOGGING_URL, MFG_GRAPHQL_URL } from './constants';
import type { AdskMfgQueryResponse, AdskMfgErrorItem } from './types';

type QueryOptions = {
  operationName: string;
  query: string;
  variables?: Record<string, unknown>;
};

type AdskLogResponse = {
  status_code: number;
  operation_name: string;
  elapsed_time?: string | null;
  errors?: AdskMfgErrorItem[];
};

/**
 * Error class for ADSK MFG API errors. Includes status code and error payload information.
 */
export class AdskMfgError extends Error {
  readonly statusCode: number;
  readonly errors?: AdskMfgErrorItem[];

  constructor(
    message: string,
    statusCode: number,
    errors: AdskMfgErrorItem[] = [],
  ) {
    super(message);
    this.name = 'AdskMfgError';
    this.statusCode = statusCode;
    this.errors = errors;
  }
}

/**
 * Helper function for typescript to check if an error is an instance of AdskMfgError.
 */
export const isAdskMfgError = (error: unknown): error is AdskMfgError => {
  return error instanceof AdskMfgError;
};

/**
 * Helper function to determine if our error contains a missing resource error
 */
export const isAdskMfgResourceMissingError = (error: unknown) => {
  return (
    isAdskMfgError(error) &&
    error.errors?.some(
      (error) => error.extensions.code === 'INTERNAL_SERVER_ERROR',
    )
  );
};

/**
 * Generic function to perform a GraphQL request to the Autodesk API.
 * @param accessToken - Token for API authorization.
 * @param queryOptions - The GraphQL query options (operationName, query, variables).
 * @param request - Function to make the fetch request.
 * @param requestOptions - Additional options for the fetch request such as init, etc.
 * @param validateData - Optional function to validate the response data.
 * @param wrapErrorMessage - Optional custom error message wrapping logic.
 */
export const adskRequest = async <T, K extends string | undefined = undefined>(
  accessToken: string,
  queryOptions: QueryOptions,
  request: <P extends ResponsePayload>(
    urlPath: string,
    requestOptions?: RequestOptions & {
      retryOptions?: Partial<RetryOptions>;
    },
  ) => Promise<P>,
  requestOptions?: RequestOptions,
  validateData?: (data: AdskMfgQueryResponse<T, K>['data']) => boolean,
  wrapErrorMessage?: (msg: string) => string,
): Promise<AdskMfgQueryResponse<T, K>> => {
  // Function to log the request to the logging endpoint
  const logRequest = (adskResponse: AdskLogResponse) =>
    request(FUSION_LOGGING_URL, {
      init: {
        method: 'POST',
        body: {
          ...adskResponse,
        },
      },
      // If we error out we just want to log to console, not blow up the entire fusion app
    }).catch(console.error);

  try {
    const response = await fetch(MFG_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(queryOptions),
      ...requestOptions,
    });

    const responseData: AdskMfgQueryResponse<T, K> = await response.json();

    // Log ADSK request to DD
    logRequest({
      status_code: response.status,
      operation_name: queryOptions.operationName,
      elapsed_time: response.headers.get('X-Elapsed-Time'),
      errors: responseData.errors,
    });

    if (!response.ok) {
      throw new AdskMfgError(
        response.statusText,
        response.status,
        responseData.errors,
      );
    }

    if (
      // If we don't have a data key, it's an invalid response structure
      !responseData.data ||
      // Otherwise, test against the validateData function if provided
      (validateData && !validateData(responseData.data))
    ) {
      throw new AdskMfgError(
        'Invalid response structure',
        response.status,
        responseData.errors,
      );
    }

    return responseData;
  } catch (error) {
    console.error(error);

    if (error instanceof AdskMfgError) {
      const errorMsg = wrapErrorMessage
        ? wrapErrorMessage(error.message)
        : error.message;
      throw new AdskMfgError(errorMsg, error.statusCode, error.errors);
    }

    throw error;
  }
};
